import { useRef, useEffect, useState } from "react";
import SVG from "react-inlinesvg";
import { StyledContainer } from "./styles";
import { useStoryBibleStore } from "../../../../stores/storyBibleStore";
import { Textarea } from "src/sudokit/components/Textarea";
import { Text } from "src/sudokit/components/Text";
import { countWords } from "src/Utils";
import { cn } from "src/lib/cn";
import { applyOptimisticDraftContentEdit } from "../draftScenesPersistence";

export function ExtraInstructions({ documentId, projectId, saveDocumentScenes, disabled = false, draftStateMachine }) {
  const documentScenes = useStoryBibleStore((state) => state.documentScenes?.[documentId]);
  const getDocumentScenes = useStoryBibleStore((state) => state.getDocumentScenes);
  const setDocumentScenes = useStoryBibleStore((state) => state.setDocumentScenes);
  const setPreflightResponse = useStoryBibleStore((state) => state.setPreflightResponse);
  const [showExtraInstructions, setShowExtraInstructions] = useState(false);
  const extraInstructionsRef = useRef(null);
  const extraInstructionSource = documentScenes?.value?.extraInstructions;
  const [extraInstructions, setExtraInstructions] = useState(extraInstructionSource);
  const [isFocused, setIsFocused] = useState(false);
  const [hasSetExtraInstructions, setHasSetExtraInstructions] = useState(false);
  const wordCount = countWords(extraInstructions);

  useEffect(() => {
    // set the extra instructions that are coming in initially.  After it's set initially,
    // we ignore incoming data changes from outside the component.  This is not ideal
    // but it's what we have currently.
    if (extraInstructionSource != null && !isFocused && !hasSetExtraInstructions) {
      setExtraInstructions(extraInstructionSource);
      setHasSetExtraInstructions(true);
      if (extraInstructionSource !== "") {
        setShowExtraInstructions(true);
      }
    }
  }, [extraInstructionSource, hasSetExtraInstructions, isFocused]);

  useEffect(() => {
    if (!hasSetExtraInstructions || isFocused) return;
    setExtraInstructions(extraInstructionSource ?? "");
    if (extraInstructionSource != null && extraInstructionSource !== "") {
      setShowExtraInstructions(true);
    }
  }, [extraInstructionSource, hasSetExtraInstructions, isFocused]);

  if (!documentScenes) {
    return null;
  }

  const toggleExtraInstructions = () => {
    if (disabled) return;
    setShowExtraInstructions((prev) => !prev);
  };

  return (
    <StyledContainer
      key={documentId}
      className={cn(showExtraInstructions && "expanded", disabled && "disabled")}
    >
      <div
        className="extra-instructions-toggle"
        onClick={toggleExtraInstructions}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => e.key === "Enter" && toggleExtraInstructions()}
      >
        <SVG
          src="/assets/down-chevron.svg"
          className={cn("toggle-chevron", !showExtraInstructions && "collapsed")}
        />
        <Text weight="medium">Extra Instructions</Text>
      </div>
      {showExtraInstructions && (
        <>
          <Textarea
            ref={extraInstructionsRef}
            placeholder="Tell Sudowrite more about how to write your scenes"
            className="text-base bg-app"
            value={extraInstructions || ""}
            disabled={disabled}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);

              if (extraInstructions !== extraInstructionSource) {
                applyOptimisticDraftContentEdit({
                  documentId,
                  patch: { extraInstructions },
                  getDocumentScenes,
                  setDocumentScenes,
                  saveDocumentScenes,
                });
              }
            }}
            onChange={(e) => {
              setExtraInstructions(e.target.value);
              setPreflightResponse(null);
              draftStateMachine.events.reset();
            }}
            rightActiveText={`${wordCount} / 2000 words`}
            rightActiveTextClassName={cn(wordCount > 2000 && "text-app-danger-color")}
            autoFocus={false}
          />
        </>
      )}
    </StyledContainer>
  );
}

export default ExtraInstructions;
